import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Space, Tag, Typography } from "@bedrock/components";
import FoldingCard from "@mpc/common/src/components/FoldingCard";
import { TagColor } from "@bedrock/components/lib/Tag";
import DescriptionsFormItem from "#components/DescriptionsFormItem";
import CopyIcon from "#components/CopyIcon";
import classNames from "classnames/bind";
import styles from './index.less';
import { ProPopo } from "@bedrock/eed-react";
import CopyableNode from "#components/CopyableNode";
import OAProcessList from "./OAProcessList";
import { CurrencyType } from "@mpc/common/src/components";

const cx = classNames.bind(styles);

export interface OADetail {
  process_type?: string;
  /** 关联需求单ID */
  demand_id?: number;
  /** OaId */
  oa_id?: string;
  /** oa流程地址 */
  oa_url?: string;
  /** 提交人Email */
  submit_user_email?: string;
  /** 提交人 */
  submit_user_name?: string;
  /** 提交时间 */
  submit_time?: string;
  oa_status?: string;
  /** oa拒绝原因 */
  oa_reject_reason?: string;
  /** 方案综合评分法对应的2级单号 */
  multi_quota_plan_demand_id?: number;
  /** 超额审批金额，使用的需求单的原币种 */
  over_amount_settle_price?: number;
  /** 超额审批原因 */
  over_amount_reason?: string;
  /** 超出的金额，使用人民币 */
  over_amount_cny?: number;
}

export interface OAApproveCardProps {
  processType: string;
  oaList: OADetail[];
  showTitle?: boolean;
  group_type?: string;
  processTypeMap?: Record<string, ProcessConfig>;
  style?: React.CSSProperties;
  className?: string;
  titleRightSlot?: React.ReactNode;
}

export interface OANode {
  /** 审批节点名 */
  node_name?: string;
  /** 审批过程明细 */
  oa_audit_nodes?: OaAuditNodes[];
}

export interface OaAuditNodes {
  /** 审批人 */
  audit_user?: string;
  /** 接收人 */
  receive_user?: string;
  /** 操作 */
  op?: string;
  /** 审批时间 */
  audit_time?: string;
  /** 审批建议 */
  suggest?: string;
  op_type?: string;
}

export interface ProcessConfig {
  type: string;
  typeName: string;
  getRecordApi: (params: any) => Promise<any>, // 请求历史详情列表
  getOaProcessApi: (params: any) => Promise<any>, // 请求审批详情
  demand_type: string,
}

const OA_STATUS_MAP = {
  NOT_SUBMITTED: {
    text: '未提交',
    color: 'light-brand' as TagColor
  },
  SUBMITTED: {
    text: '已提交',
    color: 'light-blue' as TagColor
  },
  ARCHIVING: {
    text: '已归档',
    color: 'light-green' as TagColor
  },
  REJECTED: {
    text: '已驳回',
    color: 'light-red' as TagColor
  },
  SUBMITTING: {
    text: '提交中',
    color: 'light-orange' as TagColor
  }
}

const OAApproveCard: React.FC<OAApproveCardProps> = ({
  style,
  oaList,
  className,
  processType,
  group_type,
  processTypeMap,
  titleRightSlot,
}) => {
  const [OAData, setOAData] = useState<OADetail>({});
  const [form] = Form.useForm();

  const processConfig: ProcessConfig = useMemo(() => {
    return processTypeMap[processType] || processTypeMap['default'];
  }, [processType]);

  useEffect(() => {
    if (!oaList) return;
    const data = oaList?.find((item: any) => item.process_type === processType);
    if (data) {
      setOAData(data);
      form.setFieldsValue(data);
    } else {
      setOAData({ process_type: processType });
    }
  }, [oaList, processType]);

  if (!OAData?.oa_id) {
    return null
  }

  return <FoldingCard
    style={style}
    className={className}
    defaultExpanded={OAData?.oa_status === 'SUBMITTED'}
    title={
      <Space size='xs' align='center'>
        <span>{processConfig.typeName}</span>
        <Tag
          color={OA_STATUS_MAP[OAData?.oa_status || 'NOT_SUBMITTED'].color}
          text={OA_STATUS_MAP[OAData?.oa_status || 'NOT_SUBMITTED'].text}
          size='small'
        />
        {titleRightSlot}
      </Space>
    }
    extra={
      <Space size={0}>
        <span className={cx("oa-approve-card__oa-id")}>OA单号：</span>
        {OAData?.oa_id ? <CopyableNode
          value={OAData?.oa_id}
          tipToolTitle="复制"
        >
          <Button type="link" onClick={() => {OAData?.oa_url && window.open(OAData?.oa_url)}}>
            {OAData?.oa_id}
          </Button>
        </CopyableNode>
          : '--'}
      </Space>
    }
    content={<div className={cx("oa-approve-card__content")}>
      <Form form={form}>
        <DescriptionsFormItem
          items={[
            // OA 审批额外字段，在发起人前面显示（参考关联二级单的显隐逻辑，仅根据 OAData 数据判断）
            {
              label: '审批总预算',
              formItemKey: 'approval_total_budget_amount',
              componentClass: "textarea" as const,
              view: true,
              show: !!(OAData as any)?.approval_total_budget_amount,
              elementProps: {
                showComponent: () => {
                  const amount = (OAData as any)?.approval_total_budget_amount;
                  const currency = (OAData as any)?.approval_total_budget_currency;
                  
                  if (!amount) return <>--</>;
                  
                  return (
                    <CurrencyType
                      value={{
                        value: amount,
                        currency: currency || 'CNY'
                      }}
                      view={true}
                      showAmountOrder={true}
                      currencyColor="yellow"
                    />
                  );
                }
              }
            },
            {
              label: '合并审批单号',
              formItemKey: 'merged_oa_process_number',
              componentClass: "textarea" as const,
              view: true,
              show: !!(OAData as any)?.merged_oa_process_number,
              elementProps: {
                showComponent: () => {
                  const mergedNumber = (OAData as any)?.merged_oa_process_number;
                  
                  if (!mergedNumber || !Array.isArray(mergedNumber) || mergedNumber.length === 0) {
                    return <>--</>;
                  }
                  
                  // 根据API定义，merged_oa_process_number 是 string[] 类型
                  const numbers = mergedNumber.filter((num: string) => num && num.trim());
                  
                  if (numbers.length === 0) return <>--</>;
                  
                  const displayText = numbers.map((num: string) => num.startsWith('#') ? num : `#${num}`).join('、');
                  
                  return (
                    <span className="text-color-1" style={{ fontSize: '14px' }}>
                      {displayText}
                    </span>
                  );
                }
              }
            },
            {
              label: '发起人',
              formItemKey: 'submit_user_name',
              componentClass: "textarea",
              view: true,
              elementProps: {
                showComponent: () => {
                  return <ProPopo
                    name={OAData?.submit_user_name}
                    mail={OAData?.submit_user_email}
                  />
                }
              }
            },
            {
              label: '发起时间',
              formItemKey: 'submit_time',
              view: true,
              componentClass: "textarea",
            },
            {
              formItemKey: 'multi_quota_plan_demand_id',
              label: '关联二级单',
              col: 2,
              show: !!OAData?.multi_quota_plan_demand_id,
              render: (val, row: any) => {
                if (!val) return <>--</>;
                const singlePageUrl = group_type === 'channel_group' ?
                  `/#/channelDistributionWorkbench/channelDistributionGroup/projectDetailSingle/${val}`
                  :
                  `/#/marketWorkbench/marketGroup/projectDetailSingle/${val}`
                return (
                  <Space size={0}>
                    <Button
                      type="link"
                      onClick={() => window.open(singlePageUrl)}
                    >
                      #{val}
                    </Button>
                    <CopyIcon text={val} tipToolTitle="复制关联二级单单号" />
                  </Space>
                );
              },
            },
            {
              label: '驳回原因',
              formItemKey: 'oa_reject_reason',
              show: !!OAData?.oa_reject_reason,
              view: true,
              componentClass: "textarea",
              span: 2,
            },
          ]}
        />
      </Form>
      <OAProcessList processType={processType} processTypeMap={processTypeMap} oaData={OAData} />
    </div>}
  />
}

export default OAApproveCard;
